export interface Event {
  eventId: string
  slug: string
  name: string
  description: string
  active: boolean
  registrationOpenDate?: string
  registrationCloseDate?: string
  config?: EventConfig
  teamsEnabled?: boolean
  // Waitlist configuration
  waitlistEnabled?: boolean
  waitlistBypassEnabled?: boolean
}

export interface EventConfig {
  golfPriceCents: number
  banquetPriceCents: number
}

export interface Contact {
  name: string
  email: string
  phone: string
}

export interface Golfer {
  name: string
  email: string
  phone: string
  paying?: boolean
}

export interface Registration {
  registrationId: string
  eventId: string
  createdAt: string
  status: 'pending' | 'paid' | 'refunded' | 'cancelled' | 'waitlist' | 'waitlist_invited'
  primaryContact: Contact
  golfersCount: number
  banquetTicketsCount: number
  golfers: Golfer[]
  teamName?: string
  amountGolfCents: number
  amountBanquetCents: number
  amountTotalCents: number
  stripeCheckoutSessionId?: string
  stripePaymentIntentId?: string
  stripeRefundId?: string
  teamId?: string
  paymentMethod?: 'stripe' | 'check' | 'sponsorship'
  sponsorshipSubmissionId?: string
  paidGolfersCount?: number
  unpaidGolfers?: Array<{
    golferIndex: number
    name: string
    email: string
    status: 'pending' | 'registered'
    remindersSent: number
    reminderHistory: Array<{ sentAt: string }>
    linkedRegistrationId?: string
  }>
  // Waitlist fields
  waitlistPosition?: number
  waitlistToken?: string
  waitlistInvitesSent?: number
  waitlistInviteHistory?: Array<{
    sentAt: string
    emailType: 1 | 2 | 3
  }>
  // Event reminder tracking
  eventReminderSent?: boolean
  eventReminderSentAt?: string
  eventGolferRemindersSent?: boolean
  eventGolferRemindersSentAt?: string
  // Referral tracking
  referralSource?: ReferralSource
  referralSourceOther?: string
}

export interface Team {
  teamId: string
  eventId: string
  teamName: string
  createdAt: string
  notes?: string
  members?: TeamMemberDetail[]
  memberCount?: number
}

export interface TeamMemberDetail {
  registrationId: string
  primaryContact: Contact
  golfersCount: number
  golfers: Golfer[]
  addedAt: string
}

export interface Stats {
  statsId: string
  totalFundsCents: number
  totalRegistrations: number
  totalGolfers: number
  totalBanquetTickets: number
  totalBoth: number
  totalTeams: number
  manualFundsCents?: number
  manualRegistrations?: number
  manualGolfers?: number
  updatedAt: string
}

// Referral source options
export type ReferralSource =
  | 'Facebook'
  | 'Instagram'
  | 'Flyer'
  | 'Directly from a School Lunch Angels Board Member or Ambassador'
  | 'Word of Mouth'
  | 'Other'

export const REFERRAL_SOURCES: ReferralSource[] = [
  'Facebook',
  'Instagram',
  'Flyer',
  'Directly from a School Lunch Angels Board Member or Ambassador',
  'Word of Mouth',
  'Other',
]

export interface RegistrationFormData {
  eventSlug: string
  primaryContact: Contact
  golfersCount: number
  banquetTicketsCount: number
  golfers: Golfer[]
  teamName?: string
  paidGolferIndices?: number[]
  referringRegistrationId?: string
  referralSource?: ReferralSource
  referralSourceOther?: string
}

export interface CheckoutResponse {
  checkoutUrl: string
  sessionId: string
  registrationId: string
}

export interface ApiError {
  error: string
  code?: string
}

export interface RegistrationModeResponse {
  mode: 'normal' | 'waitlist' | 'closed'
  waitlistPosition?: number
  waitlistBypassEnabled?: boolean
  waitlistForced?: boolean
  capacity: {
    totalCapacity: number
    paidRegistrations: number
    paidGolfers: number
    waitlistRegistrations: number
    spotsRemaining: number
  }
}

export interface WaitlistRegistrationResponse {
  registrationId: string
  waitlistPosition: number
  message: string
}

// Sponsorship types
export interface SponsorContact {
  firstName: string
  lastName: string
  email: string
  phone: string
  businessName?: string
  displayName?: string
}

export interface SponsorshipFormData {
  eventSlug: string
  sponsor: SponsorContact
  // Hole sponsorship
  holeSelected: boolean
  holeAmountCents?: number // User-specified sponsorship amount (min $500)
  holePrizeDescription?: string
  holePrizeEstimatedAmountCents?: number
  holeTentRequested?: boolean
  // Silent sponsorship
  silentSponsorshipSelected?: boolean
  // Silent auction
  silentAuctionSelected: boolean
  silentAuctionPrizeDescription?: string
  silentAuctionPrizeAmountCents?: number
  silentAuctionProgramAddOn?: boolean
  silentAuctionLogoUrl?: string // S3 key for uploaded logo
  silentAuctionPrizePickupInfo?: string // Free-form pickup instructions
  // Program option
  programOption: 'none' | 'coupon' | 'half' | 'full'
  programOptionSource: 'none' | 'silentAuctionAddOn' | 'brandedPageSection'
  // Checklist
  tableRunnerAccepted?: boolean
  // Golf foursome
  golfFoursomeSelected?: boolean
  golfFoursomeGolfers?: Array<{ name?: string; email?: string; phone?: string }>
  // Coupon
  couponCode?: string
  couponDiscountCents?: number
  // Referral tracking
  referralSource?: ReferralSource
  referralSourceOther?: string
}

export interface SponsorshipSubmission extends SponsorshipFormData {
  submissionId: string
  eventSlug: string
  createdAt: string
  updatedAt: string
  status: 'pending' | 'paid' | 'no_payment_required'
  amountDueCents: number
  inKindTotalCents: number
  stripeSessionId?: string
  stripePaymentIntentId?: string
  adminNotes?: string
  // New fields
  holeAmountCents?: number
  silentSponsorshipSelected?: boolean
  silentAuctionLogoUrl?: string
  silentAuctionPrizePickupInfo?: string
  pickupStatus?: 'pending' | 'scheduled' | 'complete'
  pickupNote?: string
  // Golf foursome
  golfFoursomeSelected?: boolean
  golfFoursomeGolfers?: Array<{ name?: string; email?: string; phone?: string }>
  golfFoursomeAmountCents?: number
  foursomeRegistrationId?: string
}

export interface SponsorshipConfig {
  eventSlug: string
  eventName: string
  eventDate?: string
  holePriceCents: number
  programPricing: {
    coupon: number
    half: number
    full: number
  }
}

export interface SponsorshipCheckoutResponse {
  url: string
  sessionId: string
}

export interface SponsorshipStats {
  totalRevenueCents: number
  stripeRevenueCents: number
  checkRevenueCents: number
  totalInKindCents: number
  totalSponsors: number
  holeSponsorCount: number
  silentSponsorshipCount: number
  silentSponsorshipRevenueCents: number
  silentAuctionCount: number
  programOnlyCount: number
  paidCount: number
  pendingCount: number
  noPaymentRequiredCount: number
  tentRequestedCount: number
  tableclothRequestedCount: number
  tableRunnerAcceptedCount: number
  manualCount: number
  stripeCount: number
  foursomeCount: number
  foursomeGolferCount: number
}

// Ambassador types
export interface AmbassadorFormData {
  firstName: string
  lastName: string
  email: string
  phone: string
  city: string
  zip: string
  preferredContactMethod: 'email' | 'phone' | 'both'
  affiliation: string
  tshirtSize: string
  helpAreas: string[]
  statementOfInterest: string
  fundraisingExpertise: string
  agreementAccepted: boolean
  referralSource?: ReferralSource
  referralSourceOther?: string
}

export interface AmbassadorSubmission extends AmbassadorFormData {
  ambassadorId: string
  createdAt: string
  updatedAt: string
  status: 'pending' | 'paid'
  amountDueCents: number
  amountPaidCents?: number
  stripeSessionId?: string
  stripePaymentIntentId?: string
}

export interface AmbassadorCheckoutResponse {
  url: string
  sessionId: string
}

export interface AmbassadorStats {
  totalCount: number
  paidCount: number
  pendingCount: number
}

// Coupon types
export interface Coupon {
  couponId: string
  code: string
  description: string
  discountType: 'fixed' | 'percentage'
  discountValue: number
  applicableTo: 'sponsorship_foursome' | 'registration' | 'both'
  expiresAt: string | null
  active: boolean
  createdAt: string
  usageCount?: number
}

export interface CouponUsage {
  submissionId: string
  sponsorName: string
  email: string
  discountApplied: number
  createdAt: string
  status: string
}

export interface ValidateCouponResponse {
  valid: boolean
  error?: string
  coupon?: {
    code: string
    discountType: 'fixed' | 'percentage'
    discountValue: number
    description: string
  }
  discountCents?: number
}
